import React, { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { useApplyForInvoicingMutation } from '@/redux/marketerApi'

const SPEND = [
  { id: 'under_25k', label: 'Under $25K' },
  { id: '25k_100k', label: '$25K to $100K' },
  { id: '100k_500k', label: '$100K to $500K' },
  { id: '500k_plus', label: '$500K and up' },
]
const TERMS = [30, 45, 60, 90]

const Field: React.FC<{
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
  optional?: boolean
  className?: string
}> = ({ id, label, value, onChange, type = 'text', placeholder, optional, className }) => (
  <div className={cn('space-y-1.5', className)}>
    <Label htmlFor={id} className="text-gray-700">
      {label}
      {optional ? <span className="ml-1 font-normal text-gray-500">(optional)</span> : null}
    </Label>
    <Input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="border-foreground/15 bg-card text-base text-foreground placeholder:text-gray-500"
    />
  </div>
)

const Choice: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      'rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors',
      active
        ? 'border-[#C0FD72] bg-[#C0FD72] text-black'
        : 'border-foreground/15 text-gray-800 hover:bg-foreground/10',
    )}
  >
    {children}
  </button>
)

/**
 * A large brand asks to pay by invoice: who Athios bills, where, how much it expects to spend,
 * and the terms it wants. The Athios team reviews it; until then the brand can still post.
 */
export const InvoiceApplicationDialog: React.FC<{
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultLegalName?: string
  defaultContactName?: string
  defaultEmail?: string
}> = ({ open, onOpenChange, defaultLegalName, defaultContactName, defaultEmail }) => {
  const { toast } = useToast()
  const [apply, { isLoading }] = useApplyForInvoicingMutation()
  const [f, setF] = useState({
    legalName: defaultLegalName ?? '',
    billingContactName: defaultContactName ?? '',
    billingEmail: defaultEmail ?? '',
    billingPhone: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    taxId: '',
    notes: '',
  })
  const [annualSpend, setAnnualSpend] = useState<string | null>(null)
  const [terms, setTerms] = useState(30)
  const [poRequired, setPoRequired] = useState(false)
  const set = (k: keyof typeof f) => (v: string) => setF((p) => ({ ...p, [k]: v }))
  const ready =
    f.legalName.trim() &&
    f.billingContactName.trim() &&
    /\S+@\S+\.\S+/.test(f.billingEmail) &&
    f.line1.trim() &&
    f.city.trim() &&
    f.postalCode.trim()

  const submit = async () => {
    try {
      await apply({
        legalName: f.legalName,
        billingContactName: f.billingContactName,
        billingEmail: f.billingEmail,
        billingPhone: f.billingPhone || undefined,
        billingAddress: {
          line1: f.line1,
          line2: f.line2 || undefined,
          city: f.city,
          state: f.state,
          postalCode: f.postalCode,
        },
        annualSpend: annualSpend ?? undefined,
        termsRequestedDays: terms,
        poRequired,
        taxId: f.taxId || undefined,
        notes: f.notes || undefined,
      }).unwrap()
      toast({
        title: 'Application sent',
        description: 'The Athios team will review it, usually within two business days.',
      })
      onOpenChange(false)
    } catch (err: any) {
      toast({
        title: "Couldn't send",
        description: err?.data?.error ?? 'Try again in a moment.',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-foreground/10 bg-card text-foreground sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Apply to pay by invoice</DialogTitle>
          <DialogDescription className="text-gray-600">
            For established brands. Once approved, Athios invoices you on your terms as
            deliverables go out, so nothing is charged up front.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              id="legal-name"
              label="Company legal name"
              value={f.legalName}
              onChange={set('legalName')}
              className="sm:col-span-2"
            />
            <Field
              id="contact"
              label="Billing contact"
              value={f.billingContactName}
              onChange={set('billingContactName')}
            />
            <Field
              id="billing-email"
              label="Billing email"
              type="email"
              value={f.billingEmail}
              onChange={set('billingEmail')}
              placeholder="ap@company.com"
            />
            <Field
              id="billing-phone"
              label="Phone"
              value={f.billingPhone}
              onChange={set('billingPhone')}
              optional
            />
            <Field
              id="tax-id"
              label="Tax ID (EIN)"
              value={f.taxId}
              onChange={set('taxId')}
              optional
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-6">
            <Field
              id="line1"
              label="Billing address"
              value={f.line1}
              onChange={set('line1')}
              placeholder="Street address"
              className="sm:col-span-6"
            />
            <Field
              id="line2"
              label="Suite, floor"
              value={f.line2}
              onChange={set('line2')}
              optional
              className="sm:col-span-6"
            />
            <Field
              id="city"
              label="City"
              value={f.city}
              onChange={set('city')}
              className="sm:col-span-3"
            />
            <Field
              id="state"
              label="State"
              value={f.state}
              onChange={set('state')}
              className="sm:col-span-1"
            />
            <Field
              id="postal"
              label="ZIP"
              value={f.postalCode}
              onChange={set('postalCode')}
              className="sm:col-span-2"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-gray-700">Expected yearly spend on athletes</Label>
            <div className="flex flex-wrap gap-2">
              {SPEND.map((s) => (
                <Choice
                  key={s.id}
                  active={annualSpend === s.id}
                  onClick={() => setAnnualSpend(s.id)}
                >
                  {s.label}
                </Choice>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-700">Payment terms you need</Label>
            <div className="flex flex-wrap gap-2">
              {TERMS.map((t) => (
                <Choice key={t} active={terms === t} onClick={() => setTerms(t)}>
                  Net {t}
                </Choice>
              ))}
            </div>
          </div>

          <div className="flex items-start justify-between gap-4 rounded-lg border border-foreground/10 p-3">
            <div>
              <Label htmlFor="po" className="text-gray-700">
                We need a purchase order on every invoice
              </Label>
              <p className="text-xs text-gray-500">
                We'll ask for your PO number before invoicing.
              </p>
            </div>
            <Switch id="po" checked={poRequired} onCheckedChange={setPoRequired} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes" className="text-gray-700">
              Anything else? <span className="font-normal text-gray-500">(optional)</span>
            </Label>
            <Textarea
              id="notes"
              value={f.notes}
              onChange={(e) => set('notes')(e.target.value)}
              rows={2}
              placeholder="Vendor setup forms, a W-9 request, who approves invoices…"
              className="border-foreground/15 bg-card text-base text-foreground placeholder:text-gray-500"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" className="text-gray-700" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="lime" disabled={!ready || isLoading} onClick={submit}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Send application
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default InvoiceApplicationDialog
